<template>
    <div class="field">
        <label class="label">
            Sted
            <span v-if="parentRegion">
                ({{ parentRegion.name }}
                <span @click="loadParentRegion(parentRegion)">
                    <img title="Gå til området over" src="/images/btn-x-lk.svg" />
                </span> )
            </span>
            <img
                class="position"
                @click="tryGetPosition"
                title="Oppgi posisjon"
                src="/images/btn-position-lk.svg"
            />
        </label>
        <div class="control">
            <div class="select-container">
                <i :class="{ 'fa fa-spinner fa-spin': isLoading }" />
                <div v-if="isProtected">
                    <select v-model="localRegion" required class="input select">
                        <!-- DISABLES required: <option hidden disabled selected value="null">Velg område...</option>;-->
                        <option v-for="r in regions" :value="r" :key="r.id">{{ r.name }}</option>
                    </select>
                </div>
                <div v-if="!isProtected">
                    <input
                        v-model="localName"
                        @focusout="onRegionComplete"
                        list="regionSuggestions"
                        class="input"
                        type="text"
                        spellcheck="false"
                        autocomplete="off"
                        required
                        placeholder="Skriv sted..."
                    />
                    <datalist id="regionSuggestions">
                        <option v-for="r in regions" :value="r.name" :key="r.id">{{ r.name }}</option>
                    </datalist>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import api from '../../common/api';
import url from '../../common/url';
import { Bus, Stops } from '../../common/bus';

const ADMIN_AREA_4 = 4; // Municipality in Norway

export default {
    name: 'RegionSelector',
    props: { region: Object },
    data() {
        return {
            localRegion: this.region,
            localName: null,
        };
    },
    watch: {
        region(region) {
            // An existing catch finishes loading and sends it's existing region
            this.localRegion = region;
            this.localName = region.name;
            this.loadParentRegion(region);
        },
        localRegion(region) {
            // Our user updates our region through input (or localRegion is set from this.region)
            if (!region || region === this.region) return;
            if (region.level <= ADMIN_AREA_4) {
                this.localName = '';
                this.loadRegion(region);
            } else {
                this.localName = region.name;
                this.$emit('updateRegion', region); // Notify parent
                this.setDefaultRegion(region);
            }
        },
        parentRegion(parent) {
            if (parent.level >= ADMIN_AREA_4) return;
            this.localRegion = null;
            this.localName = null;
        },
    },
    computed: {
        ...mapState('regions', ['regions', 'isLoading', 'parentRegion', 'defaultRegion']),
        isProtected() {
            // Adding locations to protected areas are note allowed. Municipalities are not protected
            return !this.parentRegion || this.parentRegion.isProtected;
        },
        isNew() {
            return !this.region || this.region.id === 0;
        },
    },
    created() {
        Bus.$on(Stops.GPS_PARSED, this.loadRegionFromGps);
        if (this.localRegion) {
            this.loadParentRegion(this.localRegion);
            this.localName = this.localRegion.name;
        } else if (!this.parentRegion) this.loadDefaultRegion();
        else this.localRegion = this.defaultRegion;
        if (this.isNew) this.tryGetPosition();
    },
    beforeDestroy() {
        Bus.$off(Stops.GPS_PARSED, this.loadRegionFromGps);
    },
    methods: {
        ...mapActions('regions', ['loadParentRegion', 'loadDefaultRegion', 'loadRegion']),
        ...mapMutations('regions', ['setDefaultRegion']),
        tryGetPosition() {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(position => {
                    Bus.$emit(Stops.GPS_PARSED, `${position.coords.latitude},${position.coords.longitude}`);
                });
            } else {
                Bus.$emit(Stops.TOAST, 'Posisjon støttes ikke av din browser');
            }
        },
        onRegionComplete() {
            // The user is hopefully content with her typing - only > level4
            if (!this.localName) return;
            const region = this.regions.find(r => r.name === this.localName);
            if (region) this.localRegion = region;
            else this.createRegion(this.localName);
        },
        async createRegion(name) {
            const region = await api.post(url.getRegions(), {
                name,
                path: `${this.parentRegion.path}/${name}`,
            });
            if (region.error) return;
            this.localRegion = region;
        },
        async loadRegionFromGps(gps) {
            const region = await api.get(url.getRegionsByLocation(gps));
            if (!region || region.error) return;
            if (this.localRegion && this.localRegion.path.startsWith(region.path)) return;
            this.localRegion = region;
        },

    },
};
</script>

<style lang="scss" scoped>
.position {
    width: 1.6rem;
    height: 1.6rem;
    border: 1px solid $backgroundColorDefault;
    cursor: pointer;
    &:hover {
        border: 1px solid $borderColorActive;
    }
    border-radius: 1rem;
}
</style>
